/**
 * NOTE: This file uses `as T` type assertions for generic HTTP method return values.
 * These casts are unavoidable in a generic fetcher pattern because the HTTP response
 * is `unknown` at runtime and the caller specifies the expected type via the generic.
 * The eslint-disable below is intentional and documented.
 *
 * @see docs/ARCHITECTURE_RULES.md for exception tracking
 */
/* eslint-disable @typescript-eslint/no-unsafe-type-assertion -- needed for fetch json join */
import type { QueryParams, FetchOptions } from '@/types/api.types';
import { get, post, put, patch, del } from '@/api/httpClient';
import { API_CONFIG } from '@/config/apiConfig';
import { getSessionToken, getRegistrationToken } from '@/utils/sessionStorage';
import type { CommandAcceptedJson } from '@/types/polling.types';
import { pollUntilDone } from '@/services/polling/PollingClient';

import { FetcherService } from '../abstract/FetcherService';

/** Detect if the response looks like a CommandAcceptedJson object */
function looksLikeCommandAcceptedJson(value: unknown): value is CommandAcceptedJson {
  return (
    typeof value === 'object' &&
    value !== null &&
    'statusUrl' in value &&
    typeof (value as { statusUrl?: unknown }).statusUrl === 'string'
  );
}

type HeadersProvider = () => Record<string, string> | undefined;

const defaultHeadersProvider: HeadersProvider = () => {
  const token = getSessionToken() ?? getRegistrationToken();
  return (token !== null) ? { Authorization: `Bearer ${token}` } : undefined;
};

/**
 * Universal HTTP Fetcher with transparent CQRS polling.
 * - Automatically detects 202/commandId responses.
 * - Polls until completion.
 * - Does not auto-fetch resource URLs; returns metadata (e.g. userId).
 */
export class HttpFetcherService extends FetcherService {
  constructor(
    private readonly headersProvider: HeadersProvider = defaultHeadersProvider
  ) {
    super();
  }

  // --- GET ---
  override async get<T = unknown>(
    pathStr: string,
    params?: QueryParams,
    options?: FetchOptions
  ): Promise<T> {
    const result = await get<unknown>(pathStr, params, options);
    if (looksLikeCommandAcceptedJson(result)) {
      return await this.pollAndResolve<T>(result);
    }
    return result as T;
  }

  // --- POST ---
  override async post<T = unknown>(
    pathStr: string,
    body?: unknown,
    options?: FetchOptions & { onUploadProgress?: (e: ProgressEvent) => void }
  ): Promise<T> {
    const result = await post<unknown>(pathStr, body, options);
    if (looksLikeCommandAcceptedJson(result)) {
      return await this.pollAndResolve<T>(result);
    }
    return result as T;
  }

  // --- PATCH ---
  override async patch<T = unknown>(
    pathStr: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    const result = await patch<unknown>(pathStr, body, options);
    if (looksLikeCommandAcceptedJson(result)) {
      return await this.pollAndResolve<T>(result);
    }
    return result as T;
  }

  // --- PUT ---
  override async put<T = unknown>(
    pathStr: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    const result = await put<unknown>(pathStr, body, options);
    if (looksLikeCommandAcceptedJson(result)) {
      return await this.pollAndResolve<T>(result);
    }
    return result as T;
  }

  // --- DELETE ---
  override async del<T = unknown>(
    pathStr: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    const result = await del<unknown>(pathStr, body, options);
    if (looksLikeCommandAcceptedJson(result)) {
      return await this.pollAndResolve<T>(result);
    }
    return result as T;
  }

  /**
   * Polls until success and returns meta (e.g. userId/adminId) if available.
   * Uses API_CONFIG.baseURL internally — no baseUrl param required.
   */
  private async pollAndResolve<T>(accepted: CommandAcceptedJson): Promise<T> {
    const { status } = await pollUntilDone(accepted.statusUrl, {
      baseUrl: API_CONFIG.baseURL,
      headersProvider: () => this.headersProvider() ?? {},
    });

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- needed for type narrowing
    if (status === 'succeeded') {
      if ((accepted).meta !== null) {
        // ✅ Return metadata like { userId } or { adminId }
        return (accepted).meta as T;
      }
      // No meta -> caller will trigger refresh manually
      return undefined as unknown as T;
    }

    throw new Error('Operation did not complete successfully.');
  }
}
